import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useTheme } from '../contexts/ThemeContext'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import LanguageToggle from '../components/LanguageToggle'
import AppFooter from '../components/AppFooter'
import BrandLogo from '../components/BrandLogo'
import DashboardButton from '../components/DashboardButton'
import { formatDate } from '../lib/translations'
import { formatMoney, getApiUrl } from '../lib/utils'
import {
  Sun,
  Moon,
  Monitor,
  Check,
  ChevronRight,
  Loader2,
  CreditCard,
  LogOut,
  User as UserIcon,
  Crown,
  Sparkles,
  Wallet,
} from 'lucide-react'
import { toast } from 'sonner'

const API = getApiUrl()

const THEME_OPTIONS = [
  { value: 'light', icon: Sun, en: 'Light', ka: 'ნათელი' },
  { value: 'dark', icon: Moon, en: 'Dark', ka: 'მუქი' },
  { value: 'system', icon: Monitor, en: 'System', ka: 'სისტემური' },
]

const Profile = () => {
  const navigate = useNavigate()
  const { user, setUser, logout } = useAuth()
  const { language, t } = useLanguage()
  const { theme, setTheme } = useTheme()
  const tr = (en, ka) => (language === 'ka' ? ka : en)

  const [name, setName] = useState(user?.name || '')
  const [savingName, setSavingName] = useState(false)

  useEffect(() => {
    setName(user?.name || '')
  }, [user?.name])

  const saveName = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      toast.error(tr('Name cannot be empty', 'სახელი არ შეიძლება იყოს ცარიელი'))
      return
    }
    setSavingName(true)
    try {
      const { data } = await axios.put(
        `${API}/auth/me`,
        { name: trimmed },
        { withCredentials: true },
      )
      setUser(data)
      toast.success(tr('Profile updated', 'პროფილი განახლდა'))
    } catch (error) {
      toast.error(error.response?.data?.detail || tr('Could not save', 'ვერ შეინახა'))
    } finally {
      setSavingName(false)
    }
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const card = 'bg-white dark:bg-[#1e1438] border border-[#1b1033]/5 dark:border-white/10 rounded-bubble shadow-celebrate-sm'
  const sectionTitle = 'text-lg font-bold text-[#1b1033] dark:text-[#f4f0ff] font-display'
  const muted = 'text-sm text-gray-500 dark:text-gray-400'

  return (
    <div className="min-h-screen bg-celebrate flex flex-col">
      <header className="bg-white/80 dark:bg-[#1e1438]/80 backdrop-blur-xl border-b border-gray-200 dark:border-white/10 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            <div className="flex items-center gap-3">
              <BrandLogo />
              <DashboardButton />
            </div>
            <LanguageToggle />
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-[calc(100dvh-4rem)] max-w-3xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <h1 className="text-3xl md:text-4xl font-bold text-[#1b1033] dark:text-[#f4f0ff] font-display tracking-tight">
          {tr('Settings', 'პარამეტრები')}
        </h1>

        {/* Account */}
        <section className={`${card} p-6`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-full p-[2px] bg-grad-juicy shadow-celebrate-sm">
              <div className="flex h-full w-full items-center justify-center rounded-full bg-white dark:bg-[#1e1438] text-[#ff4d8d] font-bold text-lg">
                {(user?.name || '?').charAt(0).toUpperCase()}
              </div>
            </div>
            <div>
              <h2 className={sectionTitle}>{tr('Account', 'ანგარიში')}</h2>
              <p className={muted}>
                {user?.role === 'admin' ? tr('Administrator', 'ადმინისტრატორი') : tr('Host', 'ორგანიზატორი')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-[13px] font-medium text-gray-600 dark:text-gray-400 mb-1.5 block">
                {t.fullName || tr('Full name', 'სახელი და გვარი')}
              </Label>
              <div className="flex gap-2">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-11 bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10 rounded-xl"
                  data-testid="profile-name-input"
                />
                <Button
                  type="button"
                  onClick={saveName}
                  disabled={savingName || name.trim() === (user?.name || '')}
                  className="h-11 btn-gradient text-white rounded-xl px-5 font-semibold"
                  data-testid="profile-name-save"
                >
                  {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : tr('Save', 'შენახვა')}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-[13px] font-medium text-gray-600 dark:text-gray-400 mb-1.5 block">
                {t.email || tr('Email', 'ელ-ფოსტა')}
              </Label>
              <Input
                value={user?.email || ''}
                readOnly
                disabled
                className="h-11 bg-gray-100 dark:bg-white/5 border-gray-200 dark:border-white/10 rounded-xl text-gray-500 dark:text-gray-400"
              />
            </div>
          </div>
        </section>

        {/* Balance & referrals — the money surface lives on its own page; this
            is just the doorway to it from settings. */}
        <Link
          to="/account"
          className={`${card} flex items-center gap-4 p-6 transition-colors hover:border-[#ff4d8d]/30`}
          data-testid="account-link"
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#ff4d8d]/10 text-[#ff4d8d]">
            <Wallet className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className={sectionTitle}>{t.accountBalance}</h2>
            <p className={muted}>{t.accountSubtitle}</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-[#4a3d66] dark:text-gray-400" />
        </Link>

        {/* Appearance */}
        <section className={`${card} p-6`}>
          <h2 className={`${sectionTitle} mb-1`}>{tr('Appearance', 'გარეგნობა')}</h2>
          <p className={`${muted} mb-5`}>
            {tr('Choose how Storyfy looks. System follows your device.',
              'აირჩიეთ Storyfy-ის იერსახე. „სისტემური“ მიჰყვება მოწყობილობას.')}
          </p>

          <Label className="text-[13px] font-medium text-gray-600 dark:text-gray-400 mb-2 block">
            {tr('Theme', 'თემა')}
          </Label>
          <div className="grid grid-cols-3 gap-2 mb-5" data-testid="profile-theme">
            {THEME_OPTIONS.map((opt) => {
              const Icon = opt.icon
              const active = theme === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTheme(opt.value)}
                  className={`flex flex-col items-center gap-2 py-4 rounded-xl border-2 transition-all ${
                    active
                      ? 'border-[#ff4d8d] bg-[#ff4d8d]/[0.06]'
                      : 'border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20'
                  }`}
                  data-testid={`profile-theme-${opt.value}`}
                >
                  <Icon className={`w-5 h-5 ${active ? 'text-[#ff4d8d]' : 'text-gray-500 dark:text-gray-400'}`} />
                  <span className={`text-sm font-medium ${active ? 'text-[#1b1033] dark:text-[#f4f0ff]' : 'text-gray-600 dark:text-gray-400'}`}>
                    {language === 'ka' ? opt.ka : opt.en}
                  </span>
                  {active && <Check className="w-4 h-4 text-[#ff4d8d]" />}
                </button>
              )
            })}
          </div>

          <Label className="text-[13px] font-medium text-gray-600 dark:text-gray-400 mb-2 block">
            {t.language || tr('Language', 'ენა')}
          </Label>
          <LanguageToggle />
        </section>

        {/* Sign out */}
        <section className={`${card} p-6 flex items-center justify-between gap-3`}>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <UserIcon className="w-5 h-5 shrink-0 text-gray-500 dark:text-gray-400" />
            <span className="truncate text-gray-700 dark:text-gray-300" title={user?.email}>{user?.email}</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={handleLogout}
            className="shrink-0 text-gray-600 dark:text-gray-400 hover:text-red-600"
            data-testid="profile-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t.signOut || tr('Sign out', 'გასვლა')}
          </Button>
        </section>
      </main>

      <AppFooter maxWidth="max-w-3xl" />
    </div>
  )
}

export default Profile
